import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from 'store';
import { createCustomers, Customer, OriginalCustomer } from 'types/customer';

const customerStatus = ['all', 'using', 'canceled'] as const;
const statusTypeGuard = (e: string): e is typeof customerStatus[number] =>
  new Set<string>(customerStatus).has(e);

/**
 * 顧客一覧画面の状態
 */
interface ListState {
  /**
   * ロード中フラグ
   */
  loading: boolean;
  /**
   * 顧客一覧 for ui
   */
  customers: Customer[];
  /**
   * 選択された顧客
   */
  selectedCustomer: Customer | undefined;
  /**
   * 検索フィールドの値
   */
  searchValue: string;
  /**
   * 顧客のステータスでフィルタリングする際の値
   */
  status: typeof customerStatus[number];
  /**
   * 顧客一覧の最終更新日時
   * NOTE: この値が更新されるとお知らせがリロードされる
   */
  finalUpdateTime: string | undefined;
}

/**
 * 顧客一覧画面の初期状態
 */
const initialState: ListState = {
  loading: false,
  customers: [],
  searchValue: '',
  status: 'all',
  selectedCustomer: undefined,
  finalUpdateTime: undefined,
};

/**
 * 顧客画面の slice(reducer + action)
 */
export const listSlice = createSlice({
  name: 'list',
  initialState,
  reducers: {
    init: () => initialState,
    loadStarted: (state) => {
      state.loading = true;
    },
    loadEnded: (state) => {
      state.loading = false;
    },
    fetchCustomersSuccess: (
      state,
      action: PayloadAction<{
        originalCustomers: OriginalCustomer[];
      }>,
    ) => {
      const { originalCustomers } = action.payload;
      state.customers = createCustomers(originalCustomers);
    },
    customerSelected: (
      state,
      action: PayloadAction<{ customer: Customer }>,
    ) => {
      state.selectedCustomer = action.payload.customer;
    },
    selectedCustomerCleared: (state) => {
      state.selectedCustomer = undefined;
    },
    searchValueChanged: (state, action: PayloadAction<{ value: string }>) => {
      state.searchValue = action.payload.value;
    },
    selectCustomerStatus: (
      state,
      { payload }: PayloadAction<{ status: string }>,
    ) => {
      if (!statusTypeGuard(payload.status)) {
        return;
      }
      state.status = payload.status;
    },
    finalUpdateTimeUpdated: (
      state,
      action: PayloadAction<{ finalUpdateTime: string }>,
    ) => {
      state.finalUpdateTime = action.payload.finalUpdateTime;
    },
  },
});

/**
 * 顧客一覧画面の action
 */
export const listActions = listSlice.actions;

/**
 * 顧客一覧画面の selector（内部用）
 */
const getloading = (state: RootState) => state.customer.list.loading;
const getSelectedCustomer = (state: RootState) =>
  state.customer.list.selectedCustomer;
const getCustomers = (state: RootState) => state.customer.list.customers;
const getSearchValue = (state: RootState) => state.customer.list.searchValue;
const getStatusValue = (state: RootState) => state.customer.list.status;
const getFinalUpdateTime = (state: RootState) =>
  state.notification.list.finalUpdateTime;
// 検索ワードが顧客ID，顧客名，顧客詳細の少なくともどれか一つに部分一致する顧客の一覧を返却する
const getFilteredCustomers = createSelector(
  getCustomers,
  getSearchValue,
  getStatusValue,
  (customers, searchValue, status) => {
    const filteredCustomers: Customer[] = [];

    customers.forEach((e) => {
      let valueFlag = false;
      let statusFlag = status === 'all';

      // 検索ワードが顧客 ID に部分一致する顧客の一覧
      if (!valueFlag && e.id.toString().indexOf(searchValue) !== -1) {
        valueFlag = true;
      }

      // 検索ワードが顧客名に部分一致する顧客の一覧
      if (!valueFlag && e.name.toString().indexOf(searchValue) !== -1) {
        valueFlag = true;
      }

      // 検索ワードが顧客詳細に部分一致する顧客の一覧
      if (!valueFlag && e.detail.toString().indexOf(searchValue) !== -1) {
        valueFlag = true;
      }

      if (status === 'using' && e.status) {
        statusFlag = true;
      }

      if (status === 'canceled' && !e.status) {
        statusFlag = true;
      }

      if (valueFlag && statusFlag) {
        filteredCustomers.push(e);
      }
    });

    return filteredCustomers.reverse();
  },
);

/**
 * 顧客一覧画面の selector
 */
export const listSelectors = {
  loading: getloading,
  selectedCustomer: getSelectedCustomer,
  filteredCustomers: getFilteredCustomers,
  searchValue: getSearchValue,
  finalUpdateTime: getFinalUpdateTime,
} as const;
